import { Component, type ErrorInfo, type ReactNode } from "react";
import { reportCrash } from "@/lib/crash-report";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/** Catches render-time crashes so the app shows a recoverable screen instead of a blank page, and reports them for the production crash alert email. */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    reportCrash({
      errorType: error.name || "RenderError",
      errorMessage: error.message,
      stackTrace: error.stack ?? info.componentStack ?? undefined,
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 text-center">
          <p className="text-lg font-medium text-stone-800">Something went wrong.</p>
          <p className="text-sm text-stone-500 max-w-sm">
            We've been notified. Try reloading the page.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-5 py-2.5 bg-primary text-white rounded-full text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
